import React, { useCallback, useState } from 'react';
import useSubscriptionFeatures from './useSubscriptionFeatures';
import FeatureLockBanner from '../components/subscription/FeatureLockBanner';
import PlanUpgradeModal from '../components/subscription/PlanUpgradeModal';
import { PROFESSIONAL_PLAN_NAME } from '../components/subscription/planLockTexts';

/**
 * Cadeado por plano de uma feature (docs/features/subscription/matriz-de-planos.md).
 *
 * Regra do produto: feature fora do plano nunca some. A tela continua
 * visível, com o banner no topo; a ação (criar, editar, enviar, gerar) é
 * interceptada antes de chegar à API e abre o modal com o plano que libera
 * e o atalho para mudar de plano ("Falar com a gente" no Business).
 * O backend continua sendo a segurança (403 BLOCKED_ON_PLAN).
 *
 * @param {string|string[]} permissionKey - chave de SUBSCRIPTION_PERMISSIONS.
 *   Com array, bloqueia só quando TODAS as chaves estão desligadas (ex.: link
 *   do Google: platform_engagement e zapi_integration).
 * @param {string} featureName - nome da feature como o usuário a conhece
 *   ("A lista de espera", "Pacotes de serviços").
 * @param {string} [planName='Profissional'] - plano que libera.
 * @param {{ plural?: boolean }} [options] - `plural`: featureName no plural
 *   ("Pacotes de serviços estão disponíveis ...").
 * @returns {{
 *   locked: boolean,
 *   openUpgrade: Function, closeUpgrade: Function,
 *   guard: (fn: Function) => Function,
 *   banner: React.ReactNode|null, modal: React.ReactNode
 * }}
 *   `guard(fn)` devolve um handler que abre o modal quando bloqueado e chama
 *   `fn` quando liberado. `banner` é null quando liberado; `modal` deve ser
 *   renderizado sempre (ele mesmo controla a visibilidade).
 */
const usePlanLock = (permissionKey, featureName, planName = PROFESSIONAL_PLAN_NAME, options = {}) => {
  const plural = options.plural === true;
  const { hasPermission } = useSubscriptionFeatures();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const keys = Array.isArray(permissionKey) ? permissionKey : [permissionKey];
  const locked = keys.every((key) => !hasPermission(key));

  const openUpgrade = useCallback(() => setUpgradeOpen(true), []);
  const closeUpgrade = useCallback(() => setUpgradeOpen(false), []);

  const guard = useCallback(
    (fn) => (...args) => {
      if (locked) {
        const event = args[0];
        if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
        setUpgradeOpen(true);
        return;
      }
      if (typeof fn === 'function') fn(...args);
    },
    [locked]
  );

  const banner = locked ? (
    <FeatureLockBanner
      type="permission"
      featureName={featureName}
      planName={planName}
      plural={plural}
      onUpgradeClick={openUpgrade}
    />
  ) : null;

  const modal = (
    <PlanUpgradeModal
      isOpen={upgradeOpen}
      onClose={closeUpgrade}
      featureName={featureName}
      type="permission"
      planName={planName}
      plural={plural}
    />
  );

  return { locked, openUpgrade, closeUpgrade, guard, banner, modal };
};

export default usePlanLock;
